"use client";

import { motion, useTransform } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { axisLabelMaxWidth } from "./axis-label-budget";
import { useChartAxisSlots } from "./chart-axis-frame";
import { useChartStable, useYScale } from "./chart-context";
import { DEFAULT_Y_DOMAIN_TWEEN_MS } from "./chart-phase";
import { LINE_LOADING_PULSE_EASE } from "./line-loading-timing";
import { resolveReferenceDataRange } from "./reference-area-geometry";
import { autoContrastColor } from "./resolve-css-color";
import type { YAxisOrientation } from "./y-axis-scales";
import { normalizeYAxisId } from "./y-axis-scales";
import {
  ProjectedAxisLabels,
  useChartProjection,
  useProjectionProgress,
} from "./projection";
import {
  resolveYAxisTickCount,
  Y_AXIS_DEFAULT_TICK_COUNT,
} from "./y-axis-ticks";

const Y_AXIS_POSITION_TWEEN_MS = DEFAULT_Y_DOMAIN_TWEEN_MS;
const LABEL_GUTTER = 8;
const LABEL_CLASS =
  "text-chart-label text-xs leading-tight [overflow-wrap:anywhere]";

export interface YAxisProps {
  /** Scale group id (Recharts `yAxisId`). Default: `"left"`. */
  yAxisId?: string | number;
  /** Which side of the chart to render labels. Default: `"left"`. */
  orientation?: YAxisOrientation;
  /**
   * Approximate tick count hint for `scale.ticks()` (d3). Actual label count may differ.
   * Clamped to {@link Y_AXIS_MIN_TICK_COUNT}–{@link Y_AXIS_MAX_TICK_COUNT}. Default: 5.
   */
  numTicks?: number;
  /** Format large numbers (e.g. 1000 as "1k"). Default: true */
  formatLargeNumbers?: boolean;
  /** Custom formatter for tick labels (e.g. USD). Overrides formatLargeNumbers when set. */
  formatValue?: (value: number) => string;
  /**
   * Container background color (concrete color, not CSS var). When set and parseable,
   * axis-label text color is auto-contrasted (white on dark, neutral-900 on light).
   */
  background?: string;
}

const tickNumberFormats = new Map<string, Intl.NumberFormat>();

function tickNumberFormat(locale: string, maximumFractionDigits: number): Intl.NumberFormat {
  const key = `${locale}|${maximumFractionDigits}`;
  let format = tickNumberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, { maximumFractionDigits, useGrouping: false });
    tickNumberFormats.set(key, format);
  }
  return format;
}

function formatLabel(
  value: number,
  formatLargeNumbers: boolean,
  locale: string,
  formatValue?: (value: number) => string
): string {
  if (formatValue) {
    return formatValue(value);
  }
  if (formatLargeNumbers && value >= 1000) {
    return `${tickNumberFormat(locale, 0).format(value / 1000)}k`;
  }
  return tickNumberFormat(locale, 20).format(value);
}

function resolveTickLabelColor(
  tickY: number,
  axisId: string,
  yScale: ReturnType<typeof useYScale>,
  referenceAreas: ReturnType<typeof useChartStable>["referenceAreas"]
): string | undefined {
  for (const area of referenceAreas) {
    if (!area.axisLabelColor) {
      continue;
    }
    if (normalizeYAxisId(area.yAxisId) !== axisId) {
      continue;
    }
    const [low, high] = resolveReferenceDataRange(
      area.y1,
      area.y2,
      yScale.domain() as [number, number]
    );
    const topPixel = yScale(high) ?? 0;
    const bottomPixel = yScale(low) ?? 0;
    const bandTop = Math.min(topPixel, bottomPixel);
    const bandBottom = Math.max(topPixel, bottomPixel);
    if (tickY >= bandTop && tickY <= bandBottom) {
      return area.axisLabelColor;
    }
  }
  return undefined;
}

export function YAxis(props: YAxisProps) {
  const { containerRef } = useChartStable();
  const slots = useChartAxisSlots();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const target = slots?.yRail ?? containerRef.current;
  if (!(mounted && target)) {
    return null;
  }

  return <YAxisInner {...props} framed={slots?.yRail != null} target={target} />;
}

const YAxisInner = memo(function YAxisInner({
  yAxisId,
  orientation = "left",
  numTicks = Y_AXIS_DEFAULT_TICK_COUNT,
  formatLargeNumbers = true,
  formatValue,
  background,
  framed,
  target,
}: YAxisProps & { framed: boolean; target: HTMLElement }) {
  const { margin, referenceAreas, innerHeight } = useChartStable();
  const yScale = useYScale(yAxisId);
  const projection = useChartProjection();
  const projectionProgress = useProjectionProgress(projection);
  const htmlOpacity = useTransform(projectionProgress, [0, 0.4], [1, 0]);
  const isLeft = orientation === "left";
  const axisId = normalizeYAxisId(yAxisId);
  const contrastColor = useMemo(() => autoContrastColor(background), [background]);
  const { intlLocale } = useViewerLanguage();

  const railWidth = isLeft ? margin.left : margin.right;
  const maxWidth = axisLabelMaxWidth(
    "yAxisTick",
    framed ? undefined : railWidth - LABEL_GUTTER
  );

  const ticks = useMemo(() => {
    const tickValues = yScale.ticks(resolveYAxisTickCount(numTicks));
    return tickValues.map((value) => {
      const y = (yScale(value) ?? 0) + margin.top;
      return {
        value,
        y,
        label: formatLabel(value, formatLargeNumbers, intlLocale, formatValue),
        labelColor: resolveTickLabelColor(
          y - margin.top,
          axisId,
          yScale,
          referenceAreas
        ),
      };
    });
  }, [
    yScale,
    margin.top,
    numTicks,
    formatLargeNumbers,
    formatValue,
    intlLocale,
    axisId,
    referenceAreas,
  ]);

  const labelStyle = (labelColor: string | undefined) => ({
    maxWidth,
    ...(labelColor
      ? { color: labelColor }
      : contrastColor
        ? { color: contrastColor }
        : {}),
  });

  const tickRows = ticks.map((tick) => (
    <div
      className={cn(
        "absolute flex items-center",
        isLeft ? "justify-end pr-2 text-right" : "justify-start pl-2 text-left"
      )}
      key={tick.value}
      style={{
        top: tick.y,
        transform: "translateY(-50%)",
        transition: `top ${Y_AXIS_POSITION_TWEEN_MS}ms cubic-bezier(${LINE_LOADING_PULSE_EASE.join(", ")})`,
        ...(isLeft ? { right: 0 } : { left: 0 }),
      }}
    >
      <span className={LABEL_CLASS} style={labelStyle(tick.labelColor)}>
        {tick.label}
      </span>
    </div>
  ));

  const rootClassName = framed
    ? "pointer-events-none relative"
    : "pointer-events-none absolute inset-0";

  const content = framed ? (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "invisible h-0 overflow-hidden",
          isLeft ? "pr-2 text-right" : "pl-2 text-left"
        )}
      >
        {ticks.map((tick) => (
          <div className={LABEL_CLASS} key={tick.value} style={{ maxWidth }}>
            {tick.label}
          </div>
        ))}
      </div>
      {tickRows}
    </>
  ) : (
    <div
      className="absolute top-0 bottom-0"
      style={{
        ...(isLeft
          ? { left: 0, width: railWidth }
          : { right: 0, width: railWidth }),
      }}
    >
      {tickRows}
    </div>
  );

  if (!projection) {
    return createPortal(<div className={rootClassName}>{content}</div>, target);
  }

  return (
    <>
      {createPortal(
        <motion.div className={rootClassName} style={{ opacity: htmlOpacity }}>
          {content}
        </motion.div>,
        target
      )}
      {isLeft && (
        <ProjectedAxisLabels
          axis="z"
          labels={ticks.map((tick) => ({
            key: String(tick.value),
            text: tick.label,
            x: 0,
            y: projection.frame.depth,
            z: innerHeight - (tick.y - margin.top),
          }))}
        />
      )}
    </>
  );
});

YAxis.displayName = "YAxis";

export default YAxis;
